import "./PrototypingPage.scss";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormSwitch,
} from "@coreui/react";
import { CSpinnerText } from "@tradingdroid/frontends-components";
import { useBacktests, useExchangeSymbols, useExchanges, useStrategies } from "@src/hooks/redux";
import { useToastActions } from "@src/hooks/useToasts";
import { tdApi } from "@src/services/api/tdApi";
import { buildDefaultConfig } from "@src/utils/configBuilder";
import { delay, formatToISO8601WithTimezone } from "@src/utils/dateUtils";
import { combineFilters, createFilter } from "@tradingdroid/api-client";
import dayjs from "dayjs";

import { CDateTimePicker } from "@src/components/CDateTimePicker";
import { RunBlock } from "@src/components/RunResults/RunBlock";
import { RunInfoCard } from "@src/components/RunDetails/RunInfoCard";
import { PlaceholderCard } from "@src/components/PlaceholderCard";
import { useCommonSettingsUrlParams } from "@src/hooks/useCommonSettingsUrlParams";

import type * as TDT from "@tradingdroid/frontends-types";

// Name used to auto-select ProtoStrategy
const PROTO_STRATEGY_NAME = "ProtoStrategy";

// Poll backtest job completion
const JOB_POLL_INTERVAL_MS = 2000;
const JOB_POLL_MAX_ATTEMPTS = 120; // 4 minutes max

interface Props {}

export const PrototypingPage: React.FC<Props> = () => {
  const navigate = useNavigate();
  const { showToast } = useToastActions();
  const { strategies, getStrategyById } = useStrategies();
  const { exchanges } = useExchanges();
  const { getSymbolNamesForExchange, setSymbolNamesForExchange } = useExchangeSymbols();
  const { upsertBacktest } = useBacktests();

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Form state
  const [strategyId, setStrategyId] = useState(0);
  const [exchangeName, setExchangeName] = useState("");
  const [instrument, setInstrument] = useState("");
  const [timeframe, setTimeframe] = useState("5m");
  const [startDateTime, setStartDateTime] = useState(
    dayjs().subtract(6, "month").startOf("day").format("YYYY-MM-DDTHH:mm"),
  );
  const [endDateTime, setEndDateTime] = useState(
    dayjs().subtract(1, "day").endOf("day").format("YYYY-MM-DDTHH:mm"),
  );
  const [investmentAmount, setInvestmentAmount] = useState(1000);
  const [cumulativeInvestment, setCumulativeInvestment] = useState(true);

  // Run state
  const [running, setRunning] = useState(false);
  const [backtestResult, setBacktestResult] = useState<TDT.ApiBacktest | null>(null);

  // Symbol loading
  const [loadingSymbols, setLoadingSymbols] = useState(false);
  const symbolNames = getSymbolNamesForExchange(exchangeName) ?? [];

  // Load form state from URL params (e.g. /prototyping?exchange_name=binance&instrument=XMRUSDT_PERP)
  const handleUrlParamsLoad = useCallback(
    (
      settings: Partial<{
        strategyId: number;
        exchangeName: string;
        instrument: string;
        timeframe: string;
      }>,
    ) => {
      if (settings.strategyId) setStrategyId(settings.strategyId);
      if (settings.exchangeName) setExchangeName(settings.exchangeName);
      if (settings.instrument) setInstrument(settings.instrument);
      if (settings.timeframe) setTimeframe(settings.timeframe);
    },
    [],
  );
  const { hasUrlParams } = useCommonSettingsUrlParams({ onLoad: handleUrlParamsLoad });

  // Auto-select ProtoStrategy on load (skip if URL params provided a strategy)
  useEffect(() => {
    if (hasUrlParams) return;
    if (strategyId !== 0) return;
    const protoStrategy = strategies.find((s) => s.name === PROTO_STRATEGY_NAME);
    if (protoStrategy) {
      setStrategyId(protoStrategy.id);
    }
  }, [strategies, strategyId, hasUrlParams]);

  // Fetch symbol names when exchange changes
  useEffect(() => {
    if (!exchangeName) return;
    if (getSymbolNamesForExchange(exchangeName)) return;

    let isCancelled = false;
    setLoadingSymbols(true);

    const exchangeFilter = combineFilters(createFilter("exchange", "EQ", exchangeName));
    tdApi
      .getAllSymbols({ q: exchangeFilter }, { fields: "name", limit: 500 })
      .then((symbols) => {
        if (isCancelled) return;
        const names = symbols.map((s) => s.name);
        setSymbolNamesForExchange(exchangeName, names);
      })
      .catch((err: unknown) => {
        if (isCancelled) return;
        console.error("Failed to fetch symbols:", err);
      })
      .finally(() => {
        if (!isCancelled) setLoadingSymbols(false);
      });

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchangeName]);

  const handleRunBacktest = useCallback(async () => {
    if (!strategyId) {
      showToast({ message: "Select a strategy", type: "danger" });
      return;
    }
    if (!exchangeName) {
      showToast({ message: "Select an exchange", type: "danger" });
      return;
    }
    if (!instrument) {
      showToast({ message: "Select an instrument", type: "danger" });
      return;
    }

    const strategy = getStrategyById(strategyId);
    if (!strategy) {
      showToast({ message: "Strategy not found", type: "danger" });
      return;
    }

    setRunning(true);
    setBacktestResult(null);

    try {
      const defaultConfig = buildDefaultConfig(strategy);
      const { strategy_config, exit_config } = defaultConfig as {
        strategy_config?: Record<string, unknown>;
        exit_config?: Record<string, unknown>;
      };

      const config: TDT.ApiBacktestConfig = {
        portfolio: {
          initial_capital: investmentAmount,
          cumulative_investment: cumulativeInvestment,
        },
        cost_config: {
          apply_slippage: true,
          apply_fees: true,
        },
        strategy_config: strategy_config ?? {},
        exit_config: exit_config ?? {},
      };

      const jobPayload: TDT.ApiJobCreate = {
        job_type_name: "backtest",
        config,
        end_ts: formatToISO8601WithTimezone(endDateTime),
        exchange_name: exchangeName,
        from_ts: formatToISO8601WithTimezone(startDateTime),
        period: timeframe,
        strategy_id: strategyId,
        symbol_name: instrument,
      };

      const job = await tdApi.createJob(jobPayload);
      if (!job?.id) {
        throw new Error("Failed to create backtest job");
      }

      // Poll for job completion (stay on page, no global job tracking)
      let jobResolved = false;
      for (let attempt = 1; attempt <= JOB_POLL_MAX_ATTEMPTS; attempt++) {
        if (!mountedRef.current) return;
        await delay(JOB_POLL_INTERVAL_MS);
        if (!mountedRef.current) return;

        try {
          const jobStatus = await tdApi.getJob(job.id);
          if (!mountedRef.current) return;

          if (jobStatus?.status_name === "completed") {
            const backtestId = (jobStatus.result as TDT.ApiBacktestJobResult | undefined)?.data
              ?.backtest_id;

            if (!backtestId) {
              console.error("[Prototyping] Job completed but no backtest_id found", jobStatus);
              showToast({ message: "Backtest completed but ID not found", type: "warning" });
              break;
            }

            const backtest = await tdApi.getBacktest(backtestId, {
              embed: "positions(movements(order)),job",
            });
            if (!mountedRef.current) return;

            if (backtest) {
              upsertBacktest(backtest);
              setBacktestResult(backtest);
              showToast({ message: "Backtest completed!", type: "success" });
            }
            jobResolved = true;
            break;
          }

          if (jobStatus?.status_name === "failed") {
            showToast({
              title: "Backtest failed",
              message: jobStatus.error_message || "Unknown error",
              type: "danger",
              timeout: 15000,
            });
            jobResolved = true;
            break;
          }
        } catch (error) {
          console.error(`[Prototyping] Error polling job (attempt ${attempt}):`, error);
        }
      }

      // If loop exhausted without break, the job timed out
      if (!mountedRef.current) return;
      if (!jobResolved) {
        showToast({
          title: "Timeout",
          message: "Backtest is taking too long. Check the Jobs page for status.",
          type: "warning",
          timeout: 10000,
        });
      }
    } catch (error) {
      console.error("[Prototyping] Backtest submission error:", error);
      showToast({
        title: "Error",
        message: "Failed to start backtest. Please try again.",
        type: "danger",
      });
    } finally {
      if (mountedRef.current) {
        setRunning(false);
      }
    }
  }, [
    strategyId,
    exchangeName,
    instrument,
    timeframe,
    startDateTime,
    endDateTime,
    investmentAmount,
    cumulativeInvestment,
    getStrategyById,
    showToast,
    upsertBacktest,
  ]);

  const handleViewFullBacktest = useCallback(() => {
    if (backtestResult?.id) {
      navigate(`/backtest/${backtestResult.id}`);
    }
  }, [backtestResult, navigate]);

  return (
    <div className="PrototypingPage">
      {/* Top row: controls left, metrics right */}
      <div className="PrototypingTopRow">
        {/* Left block: controls */}
        <CCard className="PrototypingConfigurationCard">
          <CCardHeader>Configuration</CCardHeader>
          <CCardBody className="p-2">
            <div className="PrototypingControls">
              <div className="ControlGroup ControlGroupWide">
                <CFormLabel>Strategy</CFormLabel>
                <CFormSelect
                  size="sm"
                  value={strategyId}
                  onChange={(e) => setStrategyId(Number(e.target.value))}
                  disabled={running}
                >
                  <option value={0}>Select strategy</option>
                  {strategies.map((strategy) => {
                    const versionLabel = strategy.version ? ` - ${strategy.version}` : "";
                    return (
                      <option key={strategy.id} value={strategy.id}>
                        {`${strategy.id}: ${strategy.name}${versionLabel}`}
                      </option>
                    );
                  })}
                </CFormSelect>
              </div>

              <div className="ControlGroup">
                <CFormLabel>Exchange</CFormLabel>
                <CFormSelect
                  size="sm"
                  value={exchangeName}
                  onChange={(e) => {
                    setExchangeName(e.target.value);
                    setInstrument("");
                  }}
                  disabled={running}
                >
                  <option value="">Select</option>
                  {exchanges.map((exchange) => (
                    <option key={exchange.name} value={exchange.name}>
                      {exchange.name}
                    </option>
                  ))}
                </CFormSelect>
              </div>

              <div className="ControlGroup">
                <CFormLabel>Symbol</CFormLabel>
                <CFormSelect
                  size="sm"
                  value={instrument}
                  onChange={(e) => setInstrument(e.target.value)}
                  disabled={running || !exchangeName || loadingSymbols}
                >
                  <option value="">
                    {!exchangeName ? "Exchange first" : loadingSymbols ? "Loading..." : "Select"}
                  </option>
                  {symbolNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </CFormSelect>
              </div>

              <div className="ControlGroup">
                <CFormLabel>Timeframe</CFormLabel>
                <CFormSelect
                  size="sm"
                  value={timeframe}
                  onChange={(e) => setTimeframe(e.target.value)}
                  disabled={running}
                >
                  <option value="1m">1m</option>
                  <option value="3m">3m</option>
                  <option value="5m">5m</option>
                  <option value="15m">15m</option>
                  <option value="30m">30m</option>
                  <option value="1h">1h</option>
                  <option value="2h">2h</option>
                  <option value="4h">4h</option>
                  <option value="6h">6h</option>
                  <option value="8h">8h</option>
                  <option value="12h">12h</option>
                  <option value="1d">1d</option>
                  <option value="3d">3d</option>
                  <option value="1w">1w</option>
                  <option value="1M">1M</option>
                </CFormSelect>
              </div>

              <div className="ControlGroupInvestment">
                <div className="ControlGroup ControlGroupInvestmentInput">
                  <CFormLabel>Investment ($)</CFormLabel>
                  <CFormInput
                    size="sm"
                    type="number"
                    min={0}
                    value={investmentAmount}
                    onChange={(e) => setInvestmentAmount(Number(e.target.value))}
                    disabled={running}
                  />
                </div>
                <div className="ControlGroup ControlGroupCumulative">
                  <CFormSwitch
                    id="protoCumulativeInvestment"
                    label="Cumulative"
                    checked={cumulativeInvestment}
                    onChange={(e) => setCumulativeInvestment(e.target.checked)}
                    disabled={running}
                  />
                </div>
              </div>

              <div className="ControlGroup ControlGroupDate">
                <CDateTimePicker
                  label="Start"
                  value={startDateTime}
                  onChange={setStartDateTime}
                  disabled={running}
                  maxDate={endDateTime}
                />
              </div>

              <div className="ControlGroup ControlGroupDate">
                <CDateTimePicker
                  label="End"
                  value={endDateTime}
                  onChange={setEndDateTime}
                  disabled={running}
                  minDate={startDateTime}
                />
              </div>

              <div className="ControlGroup RunButtonGroup">
                <CButton
                  color="primary"
                  size="sm"
                  disabled={running || !strategyId || !exchangeName || !instrument}
                  onClick={handleRunBacktest}
                >
                  {running ? <CSpinnerText text="Running..." gap="2" /> : "Run Backtest"}
                </CButton>
              </div>
            </div>
          </CCardBody>
        </CCard>

        {/* Right block: metrics summary or placeholder */}
        {backtestResult ? (
          <div className="PrototypingMetrics">
            <RunInfoCard
              run={backtestResult}
              type="backtest"
              headerAction={
                <CButton color="info" size="sm" onClick={handleViewFullBacktest}>
                  View Backtest
                </CButton>
              }
            />
          </div>
        ) : (
          <div className="PrototypingPlaceholder">
            <PlaceholderCard message="Results will appear here after running a backtest" />
          </div>
        )}
      </div>

      {/* Full-width results: chart + positions table */}
      {backtestResult && (
        <div className="PrototypingResults">
          <RunBlock run={backtestResult} type="backtest" editableIndicators />
        </div>
      )}
    </div>
  );
};
